'use client';

/* eslint-disable function-paren-newline */

import { useEffect, useRef, useState } from 'react';

import { useRegionStore } from '@northladder/services';
import { TTextLocalized } from '@northladder/utilities';

import { useSoundDetectionTranslations } from '~/translations';

// Find ways of reducing this path since we cannot index exports as they would
// be undefined at runtime.
import { IFinalSpeechToTextResponseError } from '../../../app/api/google/speech-to-text';

import {
  convertTextToSpeechWithGoogle,
  fetchLongRunningSpeechRecognitionAPI,
} from './utils';
import { useSoundClipQuery } from './queries';

export interface IFeedbackMessage {
  type: 'error' | 'info' | 'success';
  message: TTextLocalized;
}

interface IAnalyzeAudioOptions {
  /**
   * The audio stream to be analyzed.
   */
  audio: Blob;
  /**
   * The number of words to match in the sequence.
   */
  minWordsToMatch: number;
  /**
   * The array of words to listen for in an audio stream. This comes from the
   * randomly selected words from the `soundItems` list and spoken by the speech
   * to text engine.
   */
  soundWords: string[];
}

interface IWordsComparingOptions {
  /**
   * The number of words to match in the sequence.
   */
  minWordsToMatch: number;
  /**
   * The string of matching words recognized from the `result` of the API.
   */
  recognizedWords: string[];
  /**
   * The array of words to listen for in an audio stream.
   */
  soundWords: string[];
}

/**
 * The callback function to be used to play the audio stream.
 * - `audioThroughSpeechSynthesisUtterance` - Uses the browser's speech engine.
 * - `audioThroughGoogleSpeechToText` - Uses google speech to text API to play.
 */
type TPlayAudioStreamCallback = () => Promise<unknown>;

interface IUseSoundDetectionOptions {
  /**
   * This will be used to evaluate the active session before calling the
   * google auth recognition on the server side.
   */
  evaluationId: string;
  /**
   * Called when the sound detection and processing fails..
   */
  onFailedSoundDetection: () => void;
  /**
   * Called when the sound detection and processing is successful.
   */
  onSuccessfulSoundDetection: () => void;
  /**
   * This will be immediately called when the sound test test is started.
   */
  onStartSoundTest: () => void;
}

/**
 * This custom hook is used to detecting whether the device can play and listen
 * to the sound. It uses the device's microphone and speaker to test the sound
 * detection and useful in test cases of sound performance evaluation.
 */
export const useSoundDetection = ({
  evaluationId,
  onFailedSoundDetection,
  onStartSoundTest,
  onSuccessfulSoundDetection,
}: IUseSoundDetectionOptions) => {
  const {
    analyzingFailed,
    audioFileNotLoaded,
    analyzingWait,
    done,
    evaluatingMic,
    recordingFailed,
    soundNotMatchingWords,
    voiceNotReady,
  } = useSoundDetectionTranslations;

  const validationBaseURL = useRegionStore().tenantConfig?.apiBaseURL || '';

  const audioChunksRef = useRef<Blob[]>([]);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const audioRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedAudioRef = useRef<Blob | null>(null);
  const soundWordsRef = useRef<string[] | null>(null);

  // To track state when the audio to be played is being loaded
  const [isLoadingClip, setIsLoadingClip] = useState(false);
  // To track state when the audio is being played.
  const [isPlaying, setIsPlaying] = useState(false);
  // To track state when the audio is being processed.
  const [isProcessing, setIsProcessing] = useState(false);
  // Whether the sound detection process failed & requires restarting the whole journey.
  const [requiresRestart, setRequiresRestart] = useState(false);

  const [feedbackMessage, setFeedbackMessage] =
    useState<IFeedbackMessage | null>(null);
  const [analyzedWords, setAnalyzedWords] = useState<string[] | null>(null);

  /**
   * This function is called when there is an error while polling the API
   * during the speech recognition process and updates the UI with the error.
   */
  const handleUpdateUIError = (error: TTextLocalized) => {
    setFeedbackMessage({ type: 'error', message: error });
    setIsProcessing(false);
    setIsPlaying(false);
  };

  /**
   * Listen to the audio data available event and store the audio chunks.
   */
  const handleOnAudioDataAvailable = (event: BlobEvent) => {
    if (audioChunksRef.current) {
      audioChunksRef.current.push(event.data);
    }
  };

  /**
   * Save the final audio chunks once the recording is stopped.
   */
  const handleOnAudioStreamStopped = () => {
    recordedAudioRef.current = new Blob(audioChunksRef.current);
  };

  /**
   * Adds audio listeners to request for device mic and sound permissions.
   */
  const handleSetupAudioHandlers = async () => {
    await navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        audioRecorderRef.current = new MediaRecorder(stream, {
          // mimeType: 'audio/mpeg',
        });

        audioRecorderRef.current.addEventListener(
          'dataavailable',
          handleOnAudioDataAvailable
        );
        audioRecorderRef.current.addEventListener(
          'stop',
          handleOnAudioStreamStopped
        );
      })
      .catch(() => {
        setFeedbackMessage({
          type: 'error',
          message: recordingFailed,
        });
      });
  };

  /**
   * Will be called to cleanup the audio listeners and handlers when the device
   * unmounts.
   */
  const handleRemoveAudioHandlers = async () => {
    if (!audioRecorderRef.current) return;
    audioRecorderRef.current?.removeEventListener(
      'dataavailable',
      handleOnAudioDataAvailable
    );
    audioRecorderRef.current.removeEventListener(
      'stop',
      handleOnAudioStreamStopped
    );
  };

  /**
   * Will start listening to the audio stream and record the audio chunks.
   */
  async function startRecording() {
    setIsPlaying(true);
    setIsProcessing(false);

    await navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        audioRecorderRef.current = new MediaRecorder(stream, {
          mimeType: 'audio/webm',
        });

        audioRecorderRef.current.addEventListener(
          'dataavailable',
          handleOnAudioDataAvailable
        );
        audioRecorderRef.current.addEventListener(
          'stop',
          handleOnAudioStreamStopped
        );
      })
      .catch(() => {
        setFeedbackMessage({
          type: 'error',
          message: recordingFailed,
        });
      });

    if (audioRecorderRef.current) {
      try {
        audioRecorderRef.current.start();
      } catch (error) {
        setFeedbackMessage({
          type: 'error',
          message: recordingFailed,
        });
      }
      return true;
    }

    setFeedbackMessage({
      type: 'error',
      message: recordingFailed,
    });
    return false;
  }

  /**
   * Called when the audio stream is stopped and the recording is completed.
   */
  async function stopRecording() {
    if (!audioRecorderRef.current) return;

    audioRecorderRef.current.stop();
    audioRecorderRef.current.stream
      .getTracks()
      .forEach((track) => track.stop());
    setIsPlaying(false);

    await new Promise((resolve) => {
      if (audioRecorderRef.current) {
        audioRecorderRef.current.onstop = resolve;
      }
    });
  }

  /**
   * This compares the recognized words from the audio stream with the spoken
   * and the selected words to see if they match.
   */
  function checkMatchingWordsInOrder({
    minWordsToMatch,
    recognizedWords,
    soundWords,
  }: IWordsComparingOptions) {
    let matchCount = 0;
    let searchStartIndex = 0;

    for (let i = 0; i < soundWords.length && matchCount < 3; i += 1) {
      for (let j = searchStartIndex; j < recognizedWords.length; j += 1) {
        if (soundWords[i] === recognizedWords[j]) {
          matchCount += 1;
          // Next search starts from the next element
          searchStartIndex = j + 1;
          break; // Move to the next spoken word
        }
      }
    }

    return matchCount >= minWordsToMatch;
  }

  /**
   * This displays the results of the processing from the API call.
   * @param results - The string response heard from processing call.
   */
  const handleOnTranscriptionSuccess = ({
    minWordsToMatch,
    recognizedWords,
    soundWords,
  }: IWordsComparingOptions) => {
    setAnalyzedWords(recognizedWords);

    // Check if the recognized words match the selected words
    const isMatch = checkMatchingWordsInOrder({
      minWordsToMatch,
      soundWords,
      recognizedWords,
    });

    if (isMatch) {
      setFeedbackMessage({
        type: 'success',
        message: done,
      });

      // The sound detection was successful, so call this to navigate to the
      // success page.
      onSuccessfulSoundDetection();
    } else {
      setFeedbackMessage({
        type: 'error',
        message: soundNotMatchingWords,
      });

      // Failed sound detection, so call this to show appropriate feedback
      // and retry options.
      onFailedSoundDetection();
    }

    setIsProcessing(false);
  };

  /**
   * This displays the error from a failed processing from the Speech to Text API.
   * @param error - The error response from the processing call.
   */
  const handleOnTranscriptionFailed = (
    error: IFinalSpeechToTextResponseError
  ) => {
    setIsProcessing(false);

    if (error.error) {
      if (error.requiresRestart) {
        setRequiresRestart(true);
      }

      handleUpdateUIError(error.error);
    } else {
      setFeedbackMessage({
        type: 'error',
        message: analyzingFailed,
      });
    }
  };

  /**
   * This converts and audio stream blob to a base64 string.
   *
   * @param blob The blob audio stream to be converted to base64.
   */
  const audioToBase64 = (blob: Blob): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      // Clean up the undesired defaults parts of the loaded blob string like
      // Removing the data URL header at index 1.
      reader.onloadend = () =>
        resolve((reader.result as string)?.split(',')[1] || '');
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  /**
   * This receives an audio stream blob, converts it to a base64 string and
   * sends it to a processing service like ('Google Speech-to-Text' API) which
   * tries to identify the words in the audio stream.
   *
   * @param audio - The audio stream played back and listen to.
   * @param minWordsToMatch - The minimum number of words to match in the sequence.
   * @param soundWords - An array of words to listen for in an audio stream.
   */
  const analyzeAudio = async ({
    audio,
    minWordsToMatch,
    soundWords,
  }: IAnalyzeAudioOptions) => {
    setIsPlaying(false);
    setIsProcessing(true);
    setFeedbackMessage({
      type: 'info',
      message: analyzingWait,
    });

    await audioToBase64(audio)
      .then((base64Audio) =>
        fetchLongRunningSpeechRecognitionAPI({
          base64Audio,
          evaluationId,
          validationBaseURL,
        })
      )
      .then((recognizedWords: string[]) => {
        handleOnTranscriptionSuccess({
          minWordsToMatch,
          soundWords,
          recognizedWords,
        });
      })
      .catch((error: IFinalSpeechToTextResponseError) => {
        handleOnTranscriptionFailed(error);
      });
  };

  /**
   * Plays them, then listens to the audio stream and processes the result to
   * see if they match with the results of the SPEECH to TEXT recognition API.
   */
  async function handleRunSpeakerAndMicTest(
    playAudioStreamCallback: TPlayAudioStreamCallback
  ) {
    /**
     * The total number of matching words to be found in the audio stream,
     * and this will be declared successful if a sequency of those words is found.
     */
    const COUNT_OF_MATCHING_WORDS = 3;
    setTimeout(async () => {
      setAnalyzedWords(null);
      const isMediaReady = await startRecording();

      if (!isMediaReady) return;

      await playAudioStreamCallback();

      setTimeout(async () => {
        if (!audioRecorderRef.current) {
          return;
        }

        if (!audioRecorderRef.current) {
          setIsPlaying(false);
          return;
        }

        await stopRecording();

        await new Promise((resolve) => {
          if (audioRecorderRef.current) {
            audioRecorderRef.current.onstop = resolve;
          }
        });

        const audio = recordedAudioRef.current;

        recordedAudioRef.current = null;

        if (audio) {
          await analyzeAudio({
            audio,
            minWordsToMatch: COUNT_OF_MATCHING_WORDS,
            soundWords: soundWordsRef.current || [],
          });
        }
      }, 6000);
    }, 1000);
  }

  /**
   * Starts an audio record the audio playback of the given audio source and
   * returns a promise so the microphone can listen to the audio and process the
   * result.
   */
  async function playAudio(audioSrc: string) {
    return new Promise((resolve) => {
      if (!audioPlayerRef.current) return;
      /**
       * TODO: Revise this logic as it was created when the app was not using
       *  the `<audio />` tag to play the audio.
       */
      // audioPlayerRef.current.src = audioSrc;
      // audioPlayerRef.current = new Audio('/audio/version-2.wav');
      audioPlayerRef.current = new Audio(audioSrc);

      audioPlayerRef.current.onended = resolve; // Resolve the promise when audio ends
      audioPlayerRef.current.play();
    });
  }

  /**
   * Since the mobile devices speech synthesis requires actual user interaction,
   * adding this hidden button in view to prompt the browser to make speech ready.
   *
   * @deprecated Currently not being used as the `audioThroughSpeechSynthesisUtterance`
   * method working with this has been deprecated.
   */
  const handleOnSpeakNow = () => {
    const utter = new SpeechSynthesisUtterance('');
    speechSynthesis.speak(utter);
  };

  /**
   * Receives an array of words, uses the browser speech engine to speak them
   * out loud and returns a promise that resolves when the speech ends.
   *
   * @deprecated - This is not being used at the moment since it works differently
   * across different browsers. Used `playAudioFileRetrievedFromS3Bucket` below.
   */
  async function audioThroughSpeechSynthesisUtterance() {
    const words = soundWordsRef.current || [];

    const msg = new SpeechSynthesisUtterance(words.join(', '));

    try {
      if ('speechSynthesis' in window) {
        // Cleanup previous stuck voices
        await window.speechSynthesis.cancel();
        // Some mobile browsers like Safari loads voices synchronously, we force
        // a artificial wait.
        await window.speechSynthesis.getVoices();
      } else {
        setFeedbackMessage({
          type: 'error',
          message: voiceNotReady,
        });
      }

      window.speechSynthesis.speak(msg);
      return null;
    } catch (error) {
      return new Promise((resolve) => {
        msg.onend = resolve;
      });
    }
  }

  /**
   * Uses the google Text to Speech API to convert the given words to an audio.
   *
   * @deprecated - This is not being used at the moment in favour of
   * `playAudioFileRetrievedFromS3Bucket` below.
   */
  async function audioThroughGoogleSpeechToText() {
    const words = soundWordsRef.current || [];
    const audioSrc = await convertTextToSpeechWithGoogle(words.join(', '));
    await playAudio(audioSrc);
  }

  /**
   * Receives an array of words, uses the browser speech engine to speak them
   * out loud and returns a promise that resolves when the speech ends.
   *
   * @param words - The array of words to be spoken out loud.
   */
  async function playAudioFileRetrievedFromS3Bucket() {
    const words = soundWordsRef.current || [];

    const msg = new SpeechSynthesisUtterance(words.join(', '));

    try {
      if ('speechSynthesis' in window) {
        // Cleanup previous stuck voices
        // await window.speechSynthesis.cancel();
        // // Some mobile browsers like Safari loads voices synchronously, we force
        // // a artificial wait.
        // await window.speechSynthesis.getVoices();

        const audioSrc = `${validationBaseURL}/api/sound-detection/audio?words=${words.join(
          ', '
        )}`;

        await playAudio(audioSrc);
      } else {
        setFeedbackMessage({
          type: 'error',
          message: audioFileNotLoaded,
        });
      }

      window.speechSynthesis.speak(msg);
      return null;
    } catch (error) {
      return new Promise((resolve) => {
        msg.onend = resolve;
      });
    }
  }

  async function handleOnStartTestSpeakerAndMic() {
    onStartSoundTest();
    setIsPlaying(true);
    setIsProcessing(false);
    setFeedbackMessage(null);

    // Instead of removing the code, for future reference, we can use this to
    // switch between the different audio playback methods later.
    const shouldUseGoogleSpeechToText = false;
    const shouldUsSpeechSynthesis = true;

    if (shouldUseGoogleSpeechToText) {
      // Google Speech-to-Text API is not being used at the moment
      await handleRunSpeakerAndMicTest(audioThroughGoogleSpeechToText);
    } else if (shouldUsSpeechSynthesis) {
      await handleOnSpeakNow();

      await handleRunSpeakerAndMicTest(audioThroughSpeechSynthesisUtterance);
    } else {
      await handleRunSpeakerAndMicTest(playAudioFileRetrievedFromS3Bucket);
    }
  }

  /**
   * This will be called when the audio clip config is successfully fetched from
   * the API.
   */
  async function handleOnFetchingClipSuccess({
    base64Audio,
    words,
  }: {
    base64Audio: string;
    words: string[];
  }) {
    soundWordsRef.current = words;
    setIsLoadingClip(false);
    setFeedbackMessage({
      type: 'info',
      message: evaluatingMic,
    });

    const isMediaReady = await startRecording();

    if (!isMediaReady) {
      stopRecording();
      return;
    }

    // Will set up an audio recorder and play the audio clip
    await playAudio(base64Audio);

    // Will wait until the recording has completely stopped.
    await stopRecording();

    // Gets the current recorded audio stream and wipes it from the ref cache.
    // const audio = recordedAudioRef.current;

    /**
     * The total number of matching words to be found in the audio stream, and
     * this will be declared successful if a sequency of those words is found.
     */
    const COUNT_OF_MATCHING_WORDS = 3;

    if (recordedAudioRef.current) {
      await analyzeAudio({
        audio: recordedAudioRef.current,
        minWordsToMatch: COUNT_OF_MATCHING_WORDS,
        soundWords: soundWordsRef.current || [],
      });
    }
  }

  /**
   * This will be called when the audio clip config is successfully fetched from
   * the API.
   */
  function handleOnFetchingClipError(error: IFinalSpeechToTextResponseError) {
    setIsLoadingClip(false);

    if (error.error) {
      if (error.requiresRestart) {
        setRequiresRestart(true);
      }

      handleUpdateUIError(error.error);
    } else {
      setFeedbackMessage({
        type: 'error',
        message: analyzingFailed,
      });
    }
  }

  const {
    // isPending,
    // refetch,
    data,
    error: wordsError,
    isError,
  } = useSoundClipQuery({
    evaluationId,
    validationBaseURL,
    onError: handleOnFetchingClipError,
    onSuccess: handleOnFetchingClipSuccess,
  });

  useEffect(() => {
    if (data) {
      handleOnFetchingClipSuccess(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isError && wordsError) {
      handleOnFetchingClipError(
        wordsError as unknown as IFinalSpeechToTextResponseError
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordsError, isError]);

  useEffect(() => {
    setRequiresRestart(false);

    handleSetupAudioHandlers();

    return () => {
      stopRecording();
      handleRemoveAudioHandlers();

      audioChunksRef.current = [];
      audioPlayerRef.current = null;
      audioRecorderRef.current = null;
      recordedAudioRef.current = null;
      soundWordsRef.current = null;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Helps to trigger speech to text in the browser by clicking on the hidden
   *
   * @deprecated Won't be required if speech synthesis is totally removed.
   * TODO: Delete this component when the feature is no longer needed.
   */
  function HiddenSpeakButton() {
    return (
      <button
        className="absolute rounded-full border-2 bg-red-50 p-4 opacity-100"
        onClick={handleOnSpeakNow}
        type="button"
      >
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio ref={audioPlayerRef} controls src="/audio/version-2.wav" />
        ...
      </button>
    );
  }

  return {
    analyzedWords,
    audioPlayerRef,
    beginMicAndSpeakerTest: handleOnStartTestSpeakerAndMic,
    // beginMicAndSpeakerTest: refetch,
    feedbackMessage,
    isLoadingClip,
    isPlaying,
    isProcessing,
    requiresRestart,
    HiddenSpeakButton,
  };
};
